import { apiRequest } from '../apiRequest';
import type { Project } from '../../../types/api/projects';

type PortfolioProjectsResponse = { projects?: Project[] };
type BriefingsGenerateResponse = { queued?: unknown[]; skipped?: unknown[] };
type CodeIndexConfigResponse = { config?: Record<string, unknown> };
type CodeIndexQueueResponse = { queue?: unknown[] };
type GoogleDriveImportAccepted = { accepted: true; itemCount?: number };
type GoogleDriveImportResult = {
  imported?: unknown[];
  skipped?: unknown[];
  failed?: unknown[];
};

export const organisationsPortfolioAPI = {
  getPortfolioProjects: async (orgId: string): Promise<Project[]> => {
    const data = await apiRequest<PortfolioProjectsResponse>(
      `/api/organisations/${orgId}/portfolio/projects`,
      { method: 'GET' },
    );
    return data.projects ?? [];
  },

  generatePortfolioBriefings: async (
    orgId: string,
    body: { filter: 'missing' | 'stale' | 'selected'; projectIds?: string[] },
  ) => {
    const data = await apiRequest<BriefingsGenerateResponse>(
      `/api/organisations/${orgId}/portfolio/briefings/generate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
    return {
      queued: data.queued ?? [],
      skipped: data.skipped ?? [],
    };
  },

  getCodeIndexConfig: async (orgId: string) => {
    const data = await apiRequest<CodeIndexConfigResponse | Record<string, unknown>>(
      `/api/organisations/${orgId}/code-index/config`,
      { method: 'GET' },
    );
    return ('config' in data && data.config ? data.config : data) as Record<string, unknown>;
  },

  getCodeIndexQueue: async (orgId: string) => {
    const data = await apiRequest<CodeIndexQueueResponse>(
      `/api/organisations/${orgId}/code-index/queue`,
      { method: 'GET' },
    );
    return data.queue ?? [];
  },

  importGoogleDriveToProject: async (
    orgId: string,
    {
      streamId,
      projectId,
      repositoryId,
      items,
    }: {
      streamId: string;
      projectId: string;
      repositoryId: string;
      items: unknown[];
    },
  ) => {
    const data = await apiRequest<GoogleDriveImportAccepted | GoogleDriveImportResult>(
      `/api/organisations/${orgId}/google-drive/import`,
      {
        method: 'POST',
        body: JSON.stringify({ streamId, projectId, repositoryId, items }),
      },
    );
    if (data && typeof data === 'object' && 'accepted' in data && data.accepted) {
      return {
        accepted: true as const,
        itemCount: data.itemCount ?? (Array.isArray(items) ? items.length : 0),
      };
    }
    const result = data as GoogleDriveImportResult;
    return {
      imported: result.imported ?? [],
      skipped: result.skipped ?? [],
      failed: result.failed ?? [],
    };
  },
};
